import { forwardRef, useState, useEffect, useRef } from 'react'
import { Logout, UserMenu, useUserMenu, useGetIdentity, useUpdate, useRedirect, useTheme, useNotify, IconButtonWithTooltip, TextInput, SelectInput, AutocompleteInput, BooleanInput, SaveButton, SimpleForm } from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import { useMediaQuery, MenuList, MenuItem, ListItemIcon, ListItemText, Box, Typography, Chip, Button, CircularProgress } from '@mui/material'

import { getRole } from './common/roles'
import { supabaseClient } from './supabase'
import { CommonDialog } from './common/Dialog'
import { YearSelectInput } from './Design/CustomSelectInput'
import { setItemToLocalStorage, getItemFromLocalStorage } from './common/LocalStorage'
import useFacsCatalogue from './parcoursup/hooks/useFacsCatalogue'
import { clearIdentityCache } from './supabase/customAuthProvider'

import SettingsIcon from '@mui/icons-material/Settings'
import NotificationsIcon from '@mui/icons-material/Notifications'
import PersonIcon from '@mui/icons-material/Person'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { getDialogSx } from './Design/CustomInput'
const leadYears = getItemFromLocalStorage('leadYears') ?? []

const ConfigurationDialog = ({ openConfiguration, setOpenConfiguration, onClose }) => {
  const localCurrentYear = getItemFromLocalStorage('localCurrentYear')
  const isCurrentYear = !localCurrentYear || localCurrentYear?.id === process.env.REACT_APP_CURRENT_YEAR_ID

  const handleGoToCurrentYear = () => {
    localStorage.removeItem('localCurrentYear')
    window.location.reload(true)
  }

  return (
    <CommonDialog open={openConfiguration} handleClose={() => { setOpenConfiguration(false); onClose() }} title="Années scolaires" size="xs" minHeight="50px">
      { !isCurrentYear && (
        <Button
          variant="contained"
          fullWidth
          sx={{ mb: 2 }}
          onClick={handleGoToCurrentYear}
        >
          Année en cours
        </Button>
      )}

      <YearSelectInput
        label="Année scolaire à afficher"
        minWidth="200px"
        options={leadYears || [
          { id: process.env.REACT_APP_CURRENT_YEAR_ID, name: '2025-2026' },
          { id: process.env.REACT_APP_PREVIOUS_YEAR_ID, name: '2024-2025' },
        ]}
      />
    </CommonDialog>
  )
}

const FacDependentFields = ({ facs }) => {
  const { setValue } = useFormContext()
  const facId = useWatch({ name: 'faculte_id' })
  const prevFacRef = useRef(facId)

  useEffect(() => {
    if ( prevFacRef.current !== facId ) {
      setValue('mineure_id', '')
      setValue('grand_admis', false)
      prevFacRef.current = facId
    }
  }, [facId, setValue])

  const mineures = facs.find(f => f.id === facId)?.mineures || []
  return (
    <>
      {mineures.length > 0 && (
        <SelectInput source="mineure_id" label="Mineure" fullWidth choices={mineures} emptyText="Aucune" />
      )}
      {facId && (
        <BooleanInput source="grand_admis" label="Grand admis" />
      )}
    </>
  )
}

const ProfileDialog = ({ open, setOpen, onClose, identity, refetchIdentity }) => {
  const notify = useNotify()
  const { facs } = useFacsCatalogue()
  const [userIdCopied, setUserIdCopied] = useState(false)

  const handleCopyUserId = () =>
    navigator.clipboard.writeText(identity?.id)
      .then(() => {
        setUserIdCopied(true)
        setTimeout(() => setUserIdCopied(false), 3000)
      })
      .catch(err => {
        console.error('Failed to copy: ', err)
      })

  const handleSave = async (values) => {
    const facId = values.faculte_id || null
    const validMineures = facs.find(f => f.id === facId)?.mineures || []
    const mineureId = facId && validMineures.some(m => m.id === values.mineure_id) ? values.mineure_id : null

    try {
      const { error } = await supabaseClient
        .from('profiles')
        .update({
          first_name: values.first_name,
          last_name: values.last_name,
          faculte_id: facId,
          mineure_id: mineureId,
          grand_admis: facId ? (values.grand_admis || false) : false,
          has_licence: values.has_licence || false,
          has_car: values.has_car || false,
        })
        .eq('id', identity.id)

      if (error) throw error
      clearIdentityCache()
      await refetchIdentity()
      setOpen(false)
      onClose()
    } catch (err) {
      console.error('Profile save error:', err)
      notify('Erreur lors de la mise à jour', { type: 'error' })
    }
  }

  return (
    <CommonDialog open={open} handleClose={() => { setOpen(false); onClose() }} title="Mon profil" icon={<PersonIcon />} size="sm" minHeight="50px" displayDialogActions={false}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            borderRadius: 1,
          },
          p: 1,
          mb: 1,
        }}
        onClick={handleCopyUserId}
      >
        <Box sx={{ flexGrow: 1 }}>
          <b>Mon ID :</b> {identity?.center?.id}
        </Box>

        <IconButtonWithTooltip label={userIdCopied ? "Copié !" : "Copier l'ID"} size="small" edge="end">
          {userIdCopied ? <CheckCircleIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
        </IconButtonWithTooltip>
      </Box>

      {identity && open && (
        <SimpleForm
          record={{
            first_name: identity.first_name || '',
            last_name: identity.last_name || '',
            faculte_id: identity.faculte_id || '',
            mineure_id: identity.mineure_id || '',
            grand_admis: identity.grand_admis || false,
            has_licence: identity.has_licence || false,
            has_car: identity.has_car || false,
          }}
          onSubmit={handleSave}
          toolbar={<SaveButton />}
          sx={{ p: 0, m: 0 }}
        >
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <TextInput source="first_name" label="Prénom" fullWidth />
            <TextInput source="last_name" label="Nom" fullWidth />
          </Box>

          <AutocompleteInput
            source="faculte_id"
            label="Faculté"
            fullWidth
            choices={facs.map(f => ({ id: f.id, name: f.label }))}
          />

          <FacDependentFields facs={facs} />

          <Box sx={{ display: 'flex', gap: 2, mb: 2, width: '100%' }}>
            <BooleanInput source="has_licence" label="Permis de conduire" helperText={false} />
            <BooleanInput source="has_car" label="Véhiculé(e)" helperText={false} />
          </Box>
        </SimpleForm>
      )}
    </CommonDialog>
  )
}

const NotificationDialog = ({ open, setOpen, onClose, profileNotifications, setNumOfNotifications }) => {
  const redirect = useRedirect()
  const [update] = useUpdate()
  const [theme] = useTheme()
  const isDark = theme === 'dark'

  // Filter notifications to show in the dialog
  const now = new Date()
  const todayDate = now.toISOString().split('T')[0]
  const visibleNotifications = profileNotifications.filter(entry => {
    if ( ! entry.visible ) return false
    if (entry.type === 'callback' && entry.date) {
      const entryDate = new Date(entry.date).toISOString().split('T')[0]
      return entryDate <= todayDate // past or today
    }
    
    return entry.type !== 'callback'
  })

  const getNotificationColor = (type) => {
    switch (type) {
      case 'assignation':
        return 'success'
      case 'callback':
        return 'warning'
      case 'taskReminder':
        return 'primary'
      case 'warning':
        return null
      default:
        return 'info'
    }
  }

  const handleDeleteNotification = (notificationId, index) => {
    // Find the index in the original array
    const originalIndex = profileNotifications.findIndex(n => n.id === notificationId)
    if (originalIndex !== -1) {
      profileNotifications.splice(originalIndex, 1)
    }

    update('profiles_notifications',
    {
      id: notificationId,
      data: { visible: false },
      previousData: { visible: true }
    })

    setNumOfNotifications(prev => Math.max(0, prev - 1))
  }

  const [clearing, setClearing] = useState(false)

  const handleClearAll = async () => {
    if (visibleNotifications.length === 0) return

    setClearing(true)
    profileNotifications.length = 0
    setNumOfNotifications(0)

    const profileId = visibleNotifications[0].profile_id
    await supabaseClient
      .from('profiles_notifications')
      .update({ visible: false })
      .eq('profile_id', profileId)
      .eq('visible', true)

    setClearing(false)
  }

  const handleNotificationURL = (type, url) => {
    if (type === 'callback') {
      setOpen(false)
      redirect(url)
    }
  }

  // Mark all visible notifications as viewed when dialog opens
  useEffect(() => {
    if (open && visibleNotifications.length > 0) {
      const markAsViewed = async () => {
        const notificationsToUpdate = visibleNotifications
          .filter(n => !n.viewed)
          .map(n => ({
            id: n.id,
            viewed: true
          }))

        if (notificationsToUpdate.length === 0) return

        const { error } = await supabaseClient
          .from('profiles_notifications')
          .upsert(notificationsToUpdate)

        if (error) {
          console.error('Error marking notifications as viewed:', error)
        }
      }

      markAsViewed()
    }
  }, [open, visibleNotifications])

  return (
    <CommonDialog
      open={open}
      handleClose={() => { setOpen(false); onClose() }}
      title="Mes notifications"
      size="xs"
      minHeight="50px"
      maxHeight="60vh"
      sx={getDialogSx(isDark)}
      compact
      displayDialogActions={false}
    >
      { visibleNotifications.length > 0 ? (
        <>
          {visibleNotifications.map((notification, index) => (
            <Chip
              clickable
              key={notification.id}
              id={`chipNotification${index}`}
              {...(getNotificationColor(notification.type) && { color: getNotificationColor(notification.type) })}
              onClick={() => handleNotificationURL(notification.type, notification.url)}
              onDelete={() => handleDeleteNotification(notification.id, index)}
              sx={{
                height: 'auto',
                marginBottom: '10px',
                padding: '10px 5px',
                borderLeft: !notification.viewed ? '5px solid #1976d2' : 'none',
                borderRadius: '2px',
                '& .MuiChip-label': {
                  display: 'block',
                  whiteSpace: 'normal',
                },
                ...(notification.type === 'warning' && {
                  backgroundColor: 'darkred',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#6b0000' },
                  '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } },
                }),
              }}
              label={
                <>
                  <strong>{notification.title}</strong><br/>
                  {notification.message}
                </>
              }
            />
          ))}

          <Button
            fullWidth
            size="small"
            disabled={clearing}
            onClick={handleClearAll}
            sx={{
              position: 'sticky',
              bottom: -20,
              mx: '-20px',
              width: 'calc(100% + 40px)',
              py: 1,
              color: 'text.secondary',
              backgroundColor: isDark ? '#222' : 'secondary.light',
              borderTop: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
              borderRadius: 0,
              '&:hover': { backgroundColor: isDark ? '#333' : '#ddd' },
            }}
          >
            {clearing ? <CircularProgress size={20} color="inherit" /> : 'Effacer toutes les notifications'}
          </Button>
        </>
      ) : <Typography variant="body1" sx={{ color: '#aaa' }}>Vous n'avez aucune notification.</Typography> }
    </CommonDialog>
  )
}

const ConfigurationMenu = forwardRef((props, ref) => {
  const { onClose } = useUserMenu()
  const [theme] = useTheme()

  const { role, numOfNotifications, newNotifications, profileNotifications, setProfileNotifications, setNumOfNotifications, setNewNotifications, identity, refetchIdentity, ...rest } = props

  const [open, setOpen] = useState(false)
  const [openConfiguration, setOpenConfiguration] = useState(false)
  const [openProfile, setOpenProfile] = useState(false)
  const resetNotifications = async () => {
    let query = []

    profileNotifications.forEach(notification => {
      const toPush = {
        id: notification.id,
        viewed: true,
      }

      query.push(toPush)
    })

    const { error } = await supabaseClient
      .from('profiles_notifications')
      .upsert(query)

    if ( error ) {
      console.error('Error updating profiles_notifications: ',error)
      return
    }
    
    setNewNotifications(false)
  }
  
  return (
    <>
      <NotificationDialog
        open={open}
        setOpen={setOpen}
        onClose={onClose}
        profileNotifications={profileNotifications}
        setNumOfNotifications={setNumOfNotifications}
      />
      
      <ConfigurationDialog
        openConfiguration={openConfiguration}
        setOpenConfiguration={setOpenConfiguration}
        onClose={onClose}
      />

      <ProfileDialog
        open={openProfile}
        setOpen={setOpenProfile}
        onClose={onClose}
        identity={identity}
        refetchIdentity={refetchIdentity}
      />

      <MenuItem
        ref={ref}
        {...rest} // It's important to pass the props to allow Material UI to manage the keyboard navigation
        onClick={() => setOpenProfile(true)}
      >
        <ListItemIcon>
          <PersonIcon fontSize="small" />
        </ListItemIcon>

        <ListItemText>Mon profil</ListItemText>
      </MenuItem>

      <MenuItem
        ref={ref}
        {...rest}
        to="/configuration"
        onClick={ () => {
          setOpen(true)
          resetNotifications()
        } }
      >
        <ListItemIcon>
          { ( numOfNotifications > 0 && newNotifications )
            ? <NotificationsIcon  fontSize="small" style={{ color: theme === 'dark' ? '#fefefe' : '#c84637' }} />
            : <NotificationsIcon fontSize="small" style={{ color: theme === 'dark' ? '#fefefe' : '#aaa'}} />
          }
        </ListItemIcon>

        <ListItemText>Notifications</ListItemText>
      </MenuItem>

      <MenuItem
        ref={ref}
        {...rest}
        to="/configuration"
        onClick={ () => setOpenConfiguration(true) }
      >
        <ListItemIcon>
          <SettingsIcon fontSize="small" />
        </ListItemIcon>

        <ListItemText>Années scolaires</ListItemText>
      </MenuItem>
    </>
  )
})

const CustomUserMenu = () => {
  const { data: identity, refetch: refetchIdentity } = useGetIdentity()
  const role = identity?.role?.grade
  const isMobile = useMediaQuery(
    theme => theme.breakpoints.down('md'),
    { noSsr: true }
  )
  const isHippocast = role === getRole('hippocast')
  const [theme] = useTheme()
  const notificationsCheckRef = useRef(getItemFromLocalStorage('notificationsCheck') ?? 0)

  const [numOfNotifications, setNumOfNotifications] = useState(0)
  const [profileNotifications, setProfileNotifications] = useState([])
  const [newNotifications, setNewNotifications] = useState(false)

  useEffect(() => {
    if ( ! identity ) return

    const getProfileNotifications = async () => {
      // check notifications every 2mn
      const currentTime = Date.now()
      if (notificationsCheckRef.current < currentTime - 120000) {
        notificationsCheckRef.current = currentTime;
        setItemToLocalStorage('notificationsCheck',currentTime)
      } else {
        return
      }

      const { data, error } = await supabaseClient
        .from('profiles_notifications')
        .select('*')
        .order('created_at',{ ascending: false })
        .eq('profile_id', identity?.id)
        .eq('visible', true)

      if (error) {
        console.error('Error fetch profiles_notifications: ',error)
        setProfileNotifications([])
        setNumOfNotifications(0)
        return
      }

      const now = new Date()
      const todayDate = now.toISOString().split('T')[0]
      const filteredNotifications = data.filter(entry => {
        if ( entry.viewed ) return false

        if (entry.type === 'callback' && entry.date) {
          const entryDate = new Date(entry.date).toISOString().split('T')[0]
          return entryDate === todayDate
        }

        return entry.type !== 'callback' && !entry.viewed
      })

      if (filteredNotifications.length > 0) {
        setNewNotifications(true)
      }

      setProfileNotifications(data)
      setNumOfNotifications(filteredNotifications.length)
    }

    getProfileNotifications()

    // Poll for new notifications every 2 minutes
    const intervalId = setInterval(getProfileNotifications, 120000)
    return () => clearInterval(intervalId)
  }, [identity])

  const MyCustomIcon = () => isMobile ? <SettingsIcon /> : (
    <>
      { ( numOfNotifications > 0 && newNotifications )
        ? <Chip
            label={numOfNotifications}
            sx={{
              borderRadius: '15px',
              marginLeft: '5px',
              height: 'auto !important',
              backgroundColor: theme === 'dark' ? '#afedf4' : '#fff',
              '& .MuiChip-label': {
                padding: '2px 6px',
                fontSize: '.8rem',
                fontWeight: 'bold',
                color: theme === 'dark' ? '#1a2e2e' : '#c84637',
              }
            }}
          />
      : null }
    </>
  )

  return (
    <UserMenu icon={<MyCustomIcon />}>
      <MenuList>
        { !isHippocast && <ConfigurationMenu
          role={role}
          numOfNotifications={numOfNotifications}
          newNotifications={newNotifications}
          profileNotifications={profileNotifications}
          setProfileNotifications={setProfileNotifications}
          setNumOfNotifications={setNumOfNotifications}
          setNewNotifications={setNewNotifications}
          identity={identity}
          refetchIdentity={refetchIdentity}
        /> }

        <Logout />
      </MenuList>
    </UserMenu>
  )
}

export default CustomUserMenu